import { useState } from 'react'
import { isBefore, subMonths } from 'date-fns'
import { defineMessages, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { addLoan } from '../../../../actions/loan'
import { NumberedLoanList } from './NumberedLoanList'
import BotHead from 'src/Components/shared/Avatar/BotHeadColored'
import { useTrackingContext } from '../../../../providers/tracking'
import { selectByStatus } from '../../../../store/selectors/home/loans'
import { TrackingEventConstants } from '../../../../constants/tracking'
import { Flex, Icon, IconButton, Button, Heading, Text } from '@homebotapp/hb-react-component-catalog'
import { HOMEOWNER_DIGEST_LOANS_MODAL } from '../../../../constants/tracking/trackingEventLocations/homeownerDigestTrackingEventLocation'
import { selectHomeSoldDate } from '../../../../store/selectors/home'

const MSG = defineMessages({
  currentLoans: {
    id: 'Mortgages.heading.currentLoans',
    defaultMessage: 'Manage Your Loans'
  },
  currentLoan: {
    id: 'Mortgages.heading.currentLoan',
    defaultMessage: 'Manage Your Loan'
  },
  pastLoans: {
    id: 'Mortgages.heading.expiredLoans',
    defaultMessage: 'Expired ({num} loans)'
  },
  pastLoan: {
    id: 'Mortgages.heading.expiredLoan',
    defaultMessage: 'Expired Loan'
  },
  pastLabel: {
    id: 'Mortgages.heading.expiredLabel',
    defaultMessage: 'Toggle expired loans visibility'
  },
  deletedLabel: {
    id: 'Mortgages.heading.deletedLabel',
    defaultMessage: 'Toggle deleted loans visibility'
  },
  deletedLoans: {
    id: 'Mortgages.heading.deletedLoans',
    defaultMessage: 'Deleted ({num} loans)'
  },
  deletedLoan: {
    id: 'Mortgages.heading.deletedLoan',
    defaultMessage: 'Deleted Loan'
  },
  none: {
    id: 'Mortgages.noLoansHTML',
    defaultMessage: 'No loans found. Add your loans to make Homebot more powerful.'
  },
  add: {
    id: 'Mortgages.loans.add',
    defaultMessage: 'Add a loan'
  }
})

export const Loans = () => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const { trackingClient } = useTrackingContext()
  const homeSoldDate = useSelector(selectHomeSoldDate)
  const { current, expired, deleted } = useSelector(selectByStatus)
  const [showPast, setShowPast] = useState(expired?.length === 1 ? true : false)
  const [showDeleted, setShowDeleted] = useState(deleted?.length === 1 ? true : false)
  const validDeletedLoans = deleted.filter(d => isBefore(subMonths(new Date(homeSoldDate), 2), new Date(d.date)))

  const handleAddLoan = () => {
    trackingClient?.clientCustomized({
      customization_type: TrackingEventConstants.CLIENT_ADDED_LOAN
    })

    dispatch(addLoan())
  }

  return (
    <Flex direction='column'>
      <Heading mb={6} textAlign='left' fontWeight='semibold'>
        {current.length === 1 ? intl.formatMessage(MSG.currentLoan) : intl.formatMessage(MSG.currentLoans)}
      </Heading>
      {current?.length > 0 ? (
        <NumberedLoanList loans={current} />
      ) : (
        <Flex direction='column' align='center'>
          <BotHead quizzical type='error' size='l' />
          <Text mt={4} mb={6} maxW='20rem' size='sm' color='warning' textAlign='center' fontStyle='italic'>
            {intl.formatMessage(MSG.none)}
          </Text>
        </Flex>
      )}
      <Button
        mt={4}
        mx='auto'
        size='sm'
        onClick={handleAddLoan}
        leftIcon={<Icon name='plus' />}
        data-qa='mortgage-add-loan-button'
        tracking={{
          guid: 'i5eatzNQSmgJuqdbjnSSXW',
          ui_context: HOMEOWNER_DIGEST_LOANS_MODAL,
          descriptor: MSG.add.defaultMessage
        }}
      >
        {intl.formatMessage(MSG.add)}
      </Button>
      {expired?.length > 0 && (
        <>
          <Flex gap={4} align='center'>
            <Heading my={6} textAlign='left' fontWeight='semibold'>
              {expired.length > 1
                ? intl.formatMessage(MSG.pastLoans, { num: expired.length })
                : intl.formatMessage(MSG.pastLoan)}
            </Heading>
            <IconButton
              size='xs'
              onClick={() => setShowPast(!showPast)}
              aria-label={intl.formatMessage(MSG.pastLabel)}
              icon={<Icon name={showPast ? 'angle-up' : 'angle-down'} />}
              tracking={{
                guid: 'cF1JLq2ETn8XA3itB7A7Gr',
                ui_context: HOMEOWNER_DIGEST_LOANS_MODAL,
                descriptor: MSG.pastLabel.defaultMessage
              }}
            />
          </Flex>
          {showPast && <NumberedLoanList loans={expired} />}
        </>
      )}
      {validDeletedLoans?.length > 0 && (
        <>
          <Flex gap={4} align='center'>
            <Heading my={6} textAlign='left' fontWeight='semibold'>
              {validDeletedLoans.length > 1
                ? intl.formatMessage(MSG.deletedLoans, { num: validDeletedLoans.length })
                : intl.formatMessage(MSG.deletedLoan)}
            </Heading>
            <IconButton
              size='xs'
              onClick={() => setShowDeleted(!showDeleted)}
              aria-label={intl.formatMessage(MSG.deletedLabel)}
              icon={<Icon name={showDeleted ? 'angle-up' : 'angle-down'} />}
              tracking={{
                guid: 'fVrvne4p11bMFQ7tSvDCEi',
                ui_context: HOMEOWNER_DIGEST_LOANS_MODAL,
                descriptor: MSG.deletedLabel.defaultMessage
              }}
            />
          </Flex>
          {showDeleted && (
            <NumberedLoanList
              loans={validDeletedLoans.sort((a, b) => (isBefore(new Date(a.date), new Date(b.date)) ? 1 : -1))}
            />
          )}
        </>
      )}
    </Flex>
  )
}
